// Display policy only: the API still decides lifecycle, thresholds and control reasons — including
// which reasons are a problem (`attention`) and which are just the next preparation step.
// The web and native copies are checked against each other by managerPresentation.test.ts.
export function isAttentionTask(task: { attention: boolean }) {
  return task.attention;
}

export function getAttentionTasks<T extends { attention: boolean; priorityRank: number }>(tasks: T[]): T[] {
  return tasks.filter(isAttentionTask).sort((a, b) => a.priorityRank - b.priorityRank);
}

// «Контроль перевозок»: every attention task, and also what the server lists there without calling it
// a problem yet — a finished shipment's documents during the driver's two hours (`control`, 30.10.2026).
// An answer from before the flag falls back to attention.
export function isControlTask(task: { attention: boolean; control?: boolean }) {
  return task.control ?? task.attention;
}

export function getControlTasks<T extends { attention: boolean; control?: boolean; priorityRank: number }>(tasks: T[]): T[] {
  return tasks.filter(isControlTask).sort((a, b) => a.priorityRank - b.priorityRank);
}

export function getAttentionLabel<T extends { code: string; attention: boolean; priorityRank: number; reason: string }>(tasks: T[]) {
  const attention = getAttentionTasks(tasks);
  const primary = attention[0];
  if (!primary) return undefined;
  const reason = primary.code === 'completion_requested'
    ? 'Водитель просит завершить рейс'
    : primary.code === 'needs_review'
      ? 'Заявке больше 72 часов'
      : primary.reason;
  return `${reason}${attention.length > 1 ? ` · ещё ${attention.length - 1}` : ''}`;
}

export type AttentionReasonGroup = {
  code: string;
  reason: string;
  shipmentIds: string[];
};

export const CONTROL_GROUP_PREVIEW_LIMIT = 3;

// A group names its reason, not one shipment's detail («· 25 мин», «· с 14:20»). Lateness reads «the time
// passed»: true both before the start and on the way, where nothing claims a truck is late.
const GROUP_REASONS: Record<string, string> = {
  // One name for the documents waited for and those overdue: the group holds both.
  missing_documents: 'Без документов',
  stale_geolocation: 'Нет свежей геопозиции',
  late_loading: 'Время погрузки прошло',
  late_unloading: 'Время выгрузки прошло',
  long_stop: 'Долгая стоянка в пути',
  long_stay_loading: 'Долгая стоянка на погрузке',
  long_stay_unloading: 'Долгая стоянка на выгрузке',
};

export function getControlReasonTone(code: string) {
  if (code === 'missing_documents') return 'info';
  return 'danger';
}

// Group the «Контроль перевозок» tasks, including secondary reasons on the same shipment.
// Server rank sets the order; counts are unique within each reason, never summed into attention.
export function groupAttentionTasks<T extends { code: string; attention: boolean; control?: boolean; priorityRank: number; reason: string; shipmentId: string }>(tasks: T[]): AttentionReasonGroup[] {
  const groups = new Map<string, AttentionReasonGroup>();
  for (const task of getControlTasks(tasks)) {
    let group = groups.get(task.code);
    if (!group) {
      group = {
        code: task.code,
        reason: GROUP_REASONS[task.code] ?? getAttentionLabel([task]) ?? task.reason,
        shipmentIds: [],
      };
      groups.set(task.code, group);
    }
    if (!group.shipmentIds.includes(task.shipmentId)) group.shipmentIds.push(task.shipmentId);
  }
  return [...groups.values()];
}

export function getWaitingStatusLabel(status: string, driverInvited = false) {
  if ((status === 'awaiting_driver' || status === 'assigned') && driverInvited) return 'Ожидает принятия';
  if (['created', 'pending', 'driver_rejected', 'awaiting_driver', 'assigned'].includes(status)) return 'Ожидает назначения';
  return undefined;
}
